import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import EditStimulasiModal from '../components/EditStimulasiModal';
import EditMPASIModal from '../components/EditMPASIModal';
import EditFeatureModal from '../components/EditFeatureModal';

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const FeatureCard = styled.div<{ $color?: string }>`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${p => p.$color || '#2DBCAF'};
  }

  &:hover button {
    opacity: 1;
  }
`;

const FeatureIcon = styled.div<{ $bg?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${p => p.$bg || 'rgba(45, 188, 175, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 16px;
`;

const FeatureName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const FeatureDesc = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0 0 16px;
  line-height: 1.5;
`;

const FeatureMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ContentCount = styled.span`
  font-size: 13px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.span<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$active ? '#22c55e' : '#ef4444'};
`;

const ComingSoonBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
`;

const PremiumBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(168, 85, 247, 0.15));
  color: #7c3aed;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
`;

const FeatureEditBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0;
  
  &:hover {
    border-color: #2DBCAF;
    color: #2DBCAF;
  }
`;

const Section = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1a1f2e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackBtn = styled.button`
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #2DBCAF;
    color: #2DBCAF;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  background: ${p => p.$active ? 'rgba(45, 188, 175, 0.1)' : '#fff'};
  color: ${p => p.$active ? '#2DBCAF' : '#64748b'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #2DBCAF;
    color: #2DBCAF;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
`;

const CategoryIcon = styled.span`
  font-size: 18px;
`;

const CategoryTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const CategoryCount = styled.span`
  font-size: 12px;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 10px;
`;

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
  gap: 16px;
  transition: all 0.15s;

  &:hover {
    background: #f1f5f9;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ContentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContentTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  margin-bottom: 4px;
`;

const ContentDesc = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ContentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const EditBtn = styled.button`
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #2DBCAF;
    color: #2DBCAF;
    background: rgba(45, 188, 175, 0.05);
  }
`;

const ToggleSwitch = styled.button<{ $active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.$active ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
`;

const EmptyCategory = styled.div`
  padding: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  background: #f8fafc;
  border-radius: 10px;
`;

interface TKFeature {
  _id: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  order: number;
  isActive: boolean;
  isPremium?: boolean;
}

interface TKContent {
  _id: string;
  featureSlug: string;
  title: string;
  description?: string;
  ageGroup?: string;
  category?: string;
  metadata?: {
    week?: number;
    day?: number;
    tips?: string;
  };
  order: number;
  isActive: boolean;
}

const featureIcons: Record<string, string> = {
  stimulasi: '🧠',
  milestone: '🎯',
  growth: '📏',
  mpasi: '🍽️',
  imunisasi: '💉',
  tips: '💡',
};

const featureColors: Record<string, string> = {
  stimulasi: 'rgba(139, 92, 246, 0.1)',
  milestone: 'rgba(59, 130, 246, 0.1)',
  growth: 'rgba(34, 197, 94, 0.1)',
  mpasi: 'rgba(245, 158, 11, 0.1)',
  imunisasi: 'rgba(239, 68, 68, 0.1)',
  tips: 'rgba(236, 72, 153, 0.1)',
};

const AGE_GROUPS = [
  { key: '0-3m', label: '0-3 bulan' },
  { key: '4-6m', label: '4-6 bulan' },
  { key: '7-9m', label: '7-9 bulan' },
  { key: '10-12m', label: '10-12 bulan' },
  { key: '1-2y', label: '1-2 tahun' },
  { key: '2-3y', label: '2-3 tahun' },
  { key: '3-4y', label: '3-4 tahun' },
  { key: '4-5y', label: '4-5 tahun' },
  { key: '5-6y', label: '5-6 tahun' },
];

const CATEGORIES = [
  { key: 'motorikKasar', label: 'Motorik Kasar', icon: '🏃' },
  { key: 'motorikHalus', label: 'Motorik Halus', icon: '✋' },
  { key: 'bicara', label: 'Bicara', icon: '💬' },
  { key: 'sosial', label: 'Sosial', icon: '👥' },
];

const TumbuhKembangPage: React.FC = () => {
  const [features, setFeatures] = useState<TKFeature[]>([]);
  const [contentCounts, setContentCounts] = useState<Record<string, number>>({});
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [content, setContent] = useState<TKContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  
  // Stimulasi specific state
  const [activeAgeGroup, setActiveAgeGroup] = useState<string>('0-3m');
  const [activeCategory, setActiveCategory] = useState<string>('motorikKasar');
  const [editingItem, setEditingItem] = useState<TKContent | null>(null);
  const [editingMPASI, setEditingMPASI] = useState<TKContent | null>(null);
  const [editingFeature, setEditingFeature] = useState<TKFeature | null>(null);
  
  // Imunisasi settings
  const [imunisasiAnjuranPremium, setImunisasiAnjuranPremium] = useState(true);
  const [savingImunisasiSetting, setSavingImunisasiSetting] = useState(false);
  const [imunisasiTab, setImunisasiTab] = useState<'wajib' | 'anjuran'>('wajib');
  const [vaccines, setVaccines] = useState<any[]>([]);
  const [loadingVaccines, setLoadingVaccines] = useState(false);
  const [editingVaccine, setEditingVaccine] = useState<any | null>(null);
  const [savingVaccine, setSavingVaccine] = useState(false);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const res = await fetch('/api/admin/tumbuh-kembang', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setFeatures(data.features || []);
          const counts: Record<string, number> = {};
          (data.contentCounts || []).forEach((c: { _id: string; count: number }) => {
            counts[c._id] = c.count;
          });
          setContentCounts(counts);
        }
      } catch (err) {
        console.error('Failed to fetch features:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatures();
  }, []);

  // Fetch immunization setting
  useEffect(() => {
    fetch('/api/settings/public/immunization.anjuran.premium')
      .then(res => res.json())
      .then(data => {
        if (data.ok) {
          setImunisasiAnjuranPremium(data.value ?? true);
        }
      })
      .catch(() => {});
  }, []);

  const toggleImunisasiAnjuranPremium = async () => {
    setSavingImunisasiSetting(true);
    try {
      const res = await fetch('/api/admin/settings/immunization.anjuran.premium', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: !imunisasiAnjuranPremium }),
      });
      if (res.ok) {
        setImunisasiAnjuranPremium(!imunisasiAnjuranPremium);
      }
    } catch (err) {
      console.error('Failed to toggle setting:', err);
    } finally {
      setSavingImunisasiSetting(false);
    }
  };

  // Fetch vaccines when imunisasi is selected
  useEffect(() => {
    if (selectedFeature === 'imunisasi') {
      setLoadingVaccines(true);
      fetch('/api/immunization/vaccines', { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.ok) {
            setVaccines(data.vaccines || []);
          }
        })
        .catch(err => console.error('Failed to fetch vaccines:', err))
        .finally(() => setLoadingVaccines(false));
    }
  }, [selectedFeature]);

  const handleSaveVaccine = async () => {
    if (!editingVaccine) return;
    setSavingVaccine(true);
    try {
      const res = await fetch(`/api/admin/immunization/vaccines/${editingVaccine._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editingVaccine),
      });
      if (res.ok) {
        const data = await res.json();
        setVaccines(vaccines.map(v => v._id === data.vaccine._id ? data.vaccine : v));
        setEditingVaccine(null);
      }
    } catch (err) {
      console.error('Failed to save vaccine:', err);
    } finally {
      setSavingVaccine(false);
    }
  };

  const fetchContent = async (featureSlug: string) => {
    setLoadingContent(true);
    setSelectedFeature(featureSlug);
    try {
      const res = await fetch(`/api/admin/tumbuh-kembang/${featureSlug}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setContent(data.content || []);
        
        // For stimulasi, set the first available age group as active
        if (featureSlug === 'stimulasi' && data.content?.length > 0) {
          const availableAgeGroups = [...new Set(data.content.map((c: TKContent) => c.ageGroup))];
          const firstAvailable = AGE_GROUPS.find(ag => availableAgeGroups.includes(ag.key));
          if (firstAvailable) {
            setActiveAgeGroup(firstAvailable.key);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch content:', err);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleToggleFeature = async (featureId: string, currentActive: boolean) => {
    try {
      const res = await fetch(`/api/admin/tumbuh-kembang/feature/${featureId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !currentActive }),
      });
      
      if (res.ok) {
        setFeatures(features.map(f => f._id === featureId ? { ...f, isActive: !currentActive } : f));
      }
    } catch (err) {
      console.error('Failed to toggle feature:', err);
    }
  };

  const handleToggleContent = async (contentId: string, currentActive: boolean) => {
    try {
      const res = await fetch(`/api/admin/tumbuh-kembang/content/${contentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !currentActive }),
      });
      
      if (res.ok) {
        setContent(content.map(c => c._id === contentId ? { ...c, isActive: !currentActive } : c));
      }
    } catch (err) {
      console.error('Failed to toggle content:', err);
    }
  };

  const handleItemSaved = (updatedItem: TKContent) => {
    setContent(content.map(c => c._id === updatedItem._id ? updatedItem : c));
    setEditingItem(null);
  };

  // Get age groups that have content
  const availableAgeGroups = AGE_GROUPS.filter(ag => 
    content.some(c => c.ageGroup === ag.key)
  );

  // Get content for current age group, grouped by category
  const getContentByCategory = (category: string) => {
    return content.filter(c => 
      c.ageGroup === activeAgeGroup && c.category === category
    );
  };

  // Check if current age group has any content
  const currentAgeGroupContent = content.filter(c => c.ageGroup === activeAgeGroup);

  if (loading) {
    return <LoadingState>Loading...</LoadingState>;
  }

  const selectedFeatureData = features.find(f => f.slug === selectedFeature);
  const isStimulasi = selectedFeature === 'stimulasi';
  const isMPASI = selectedFeature === 'mpasi';
  const isImunisasi = selectedFeature === 'imunisasi';

  const handleMPASISaved = (updatedItem: TKContent) => {
    setContent(content.map(c => c._id === updatedItem._id ? updatedItem : c));
    setEditingMPASI(null);
  };

  const handleFeatureSaved = (updatedFeature: TKFeature) => {
    setFeatures(features.map(f => f._id === updatedFeature._id ? updatedFeature : f));
    setEditingFeature(null);
  };

  // Get content for current age group and category
  const getFilteredContent = () => {
    return content.filter(c => 
      c.ageGroup === activeAgeGroup && c.category === activeCategory
    );
  };

  const renderStimulasiContent = () => {
    const filteredContent = getFilteredContent();
    
    return (
      <>
        {/* Age Group Tabs */}
        <TabsContainer>
          {AGE_GROUPS.map(ag => {
            const hasContent = content.some(c => c.ageGroup === ag.key);
            const count = content.filter(c => c.ageGroup === ag.key).length;
            return (
              <Tab
                key={ag.key}
                $active={activeAgeGroup === ag.key}
                onClick={() => setActiveAgeGroup(ag.key)}
                style={{ opacity: hasContent ? 1 : 0.5 }}
              >
                {ag.label} {hasContent && <span style={{ opacity: 0.6 }}>({count})</span>}
              </Tab>
            );
          })}
        </TabsContainer>

        {/* Category Tabs */}
        <TabsContainer style={{ borderBottom: 'none', marginBottom: 16 }}>
          {CATEGORIES.map(cat => {
            const catCount = content.filter(c => c.ageGroup === activeAgeGroup && c.category === cat.key).length;
            return (
              <Tab
                key={cat.key}
                $active={activeCategory === cat.key}
                onClick={() => setActiveCategory(cat.key)}
                style={{ 
                  opacity: catCount > 0 ? 1 : 0.5,
                  background: activeCategory === cat.key ? 'rgba(139, 92, 246, 0.1)' : undefined,
                  borderColor: activeCategory === cat.key ? '#8b5cf6' : undefined,
                  color: activeCategory === cat.key ? '#8b5cf6' : undefined,
                }}
              >
                {cat.icon} {cat.label} {catCount > 0 && <span style={{ opacity: 0.6 }}>({catCount})</span>}
              </Tab>
            );
          })}
        </TabsContainer>

        {/* Content List */}
        {filteredContent.length === 0 ? (
          <EmptyState>Tidak ada stimulasi untuk kombinasi ini</EmptyState>
        ) : (
          <ContentList>
            {filteredContent.map(item => (
              <ContentItem key={item._id}>
                <ContentInfo>
                  <ContentTitle>{item.title}</ContentTitle>
                  {item.description && (
                    <ContentDesc>{item.description}</ContentDesc>
                  )}
                </ContentInfo>
                <ContentActions>
                  <EditBtn onClick={() => setEditingItem(item)}>
                    ✏️ Edit
                  </EditBtn>
                  <ToggleSwitch
                    $active={item.isActive}
                    onClick={() => handleToggleContent(item._id, item.isActive)}
                    title={item.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                  />
                </ContentActions>
              </ContentItem>
            ))}
          </ContentList>
        )}
      </>
    );
  };

  const renderMPASIContent = () => (
    <ContentList>
      {content.map((item) => (
        <ContentItem key={item._id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <ContentInfo>
              <ContentTitle style={{ fontSize: 16 }}>{item.title}</ContentTitle>
              {item.description && (
                <ContentDesc style={{ marginTop: 4 }}>{item.description}</ContentDesc>
              )}
            </ContentInfo>
            <ContentActions>
              <EditBtn onClick={() => setEditingMPASI(item)}>
                ✏️ Edit
              </EditBtn>
              <ToggleSwitch
                $active={item.isActive}
                onClick={() => handleToggleContent(item._id, item.isActive)}
                title={item.isActive ? 'Nonaktifkan' : 'Aktifkan'}
              />
            </ContentActions>
          </div>
          
          {/* MPASI Details */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            padding: '12px 0',
            borderTop: '1px solid #e2e8f0',
          }}>
            <div>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Energi</span>
              <div style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{item.metadata?.energyKcal || '-'} Kcal</div>
            </div>
            <div>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Tekstur</span>
              <div style={{ fontSize: 14, color: '#374151' }}>{item.metadata?.texture || '-'}</div>
            </div>
            <div>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Frekuensi</span>
              <div style={{ fontSize: 14, color: '#374151' }}>{item.metadata?.frequency || '-'}</div>
            </div>
            <div>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Porsi</span>
              <div style={{ fontSize: 14, color: '#374151' }}>{item.metadata?.portionStart || '-'} → {item.metadata?.portionTarget || '-'}</div>
            </div>
          </div>
          
          {/* Tips */}
          {item.metadata?.tips && item.metadata.tips.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Tips</span>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, color: '#64748b' }}>
                {item.metadata.tips.map((tip: string, i: number) => (
                  <li key={i} style={{ marginBottom: 4 }}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </ContentItem>
      ))}
    </ContentList>
  );

  const renderDefaultContent = () => (
    <ContentList>
      {content.map((item) => (
        <ContentItem key={item._id}>
          <ContentInfo>
            <ContentTitle>{item.title}</ContentTitle>
            {item.description && (
              <ContentDesc>{item.description}</ContentDesc>
            )}
          </ContentInfo>
          <ToggleSwitch
            $active={item.isActive}
            onClick={() => handleToggleContent(item._id, item.isActive)}
            title={item.isActive ? 'Nonaktifkan' : 'Aktifkan'}
          />
        </ContentItem>
      ))}
    </ContentList>
  );

  const renderImunisasiContent = () => {
    const filteredVaccines = vaccines.filter(v => v.type === imunisasiTab);
    
    return (
    <div style={{ padding: '20px 0' }}>
      {/* Settings Section */}
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0',
        marginBottom: 20,
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1f2e', margin: '0 0 8px' }}>
          ⚙️ Pengaturan Imunisasi
        </h3>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>
          Konfigurasi akses dan fitur imunisasi
        </p>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: '#f8fafc',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
              Tab Anjuran (IDAI) - Premium Only
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {imunisasiAnjuranPremium 
                ? '🔒 Hanya user premium yang bisa mengisi checklist imunisasi anjuran'
                : '🔓 Semua user bisa mengisi checklist imunisasi anjuran'}
            </div>
          </div>
          <button
            onClick={toggleImunisasiAnjuranPremium}
            disabled={savingImunisasiSetting}
            style={{
              width: 52,
              height: 28,
              borderRadius: 14,
              border: 'none',
              cursor: savingImunisasiSetting ? 'wait' : 'pointer',
              position: 'relative',
              background: imunisasiAnjuranPremium ? '#2DBCAF' : '#e2e8f0',
              transition: 'background 0.2s',
            }}
          >
            <span style={{
              position: 'absolute',
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'white',
              top: 3,
              left: imunisasiAnjuranPremium ? 27 : 3,
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>
      </div>

      {/* Vaccine Content Management */}
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1f2e', margin: '0 0 16px' }}>
          📋 Kelola Konten Vaksin
        </h3>
        
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => setImunisasiTab('wajib')}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              background: imunisasiTab === 'wajib' ? '#22c55e' : '#f1f5f9',
              color: imunisasiTab === 'wajib' ? 'white' : '#64748b',
            }}
          >
            🏥 Wajib (Pemerintah)
          </button>
          <button
            onClick={() => setImunisasiTab('anjuran')}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              background: imunisasiTab === 'anjuran' ? '#f59e0b' : '#f1f5f9',
              color: imunisasiTab === 'anjuran' ? 'white' : '#64748b',
            }}
          >
            ⭐ Anjuran (IDAI)
          </button>
        </div>

        {/* Vaccine List */}
        {loadingVaccines ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filteredVaccines.map(vaccine => (
              <div key={vaccine._id} style={{
                padding: 16,
                background: '#f8fafc',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                      {vaccine.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                      Kode: {vaccine.code} • {vaccine.doses?.length || 0} dosis
                    </div>
                    <div style={{ fontSize: 13, color: '#475569' }}>
                      {vaccine.description}
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingVaccine({ ...vaccine })}
                    style={{
                      padding: '8px 16px',
                      background: '#2DBCAF',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    ✏️ Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Vaccine Modal */}
      {editingVaccine && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
        }} onClick={() => setEditingVaccine(null)}>
          <div style={{
            background: 'white',
            borderRadius: 16,
            width: '100%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto',
            padding: 24,
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 20px' }}>
              Edit Vaksin: {editingVaccine.name}
            </h3>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Nama Vaksin
              </label>
              <input
                type="text"
                value={editingVaccine.name}
                onChange={e => setEditingVaccine({ ...editingVaccine, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 14,
                }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Deskripsi
              </label>
              <textarea
                value={editingVaccine.description}
                onChange={e => setEditingVaccine({ ...editingVaccine, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 14,
                  resize: 'vertical',
                }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Manfaat (pisahkan dengan enter)
              </label>
              <textarea
                value={editingVaccine.benefits?.join('\n') || ''}
                onChange={e => setEditingVaccine({ ...editingVaccine, benefits: e.target.value.split('\n').filter(Boolean) })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 14,
                  resize: 'vertical',
                }}
              />
            </div>
            
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Efek Samping (pisahkan dengan enter)
              </label>
              <textarea
                value={editingVaccine.sideEffects?.join('\n') || ''}
                onChange={e => setEditingVaccine({ ...editingVaccine, sideEffects: e.target.value.split('\n').filter(Boolean) })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 14,
                  resize: 'vertical',
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setEditingVaccine(null)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Batal
              </button>
              <button
                onClick={handleSaveVaccine}
                disabled={savingVaccine}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: '#2DBCAF',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  cursor: savingVaccine ? 'wait' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {savingVaccine ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  };

  return (
    <>
      <PageHeader>
        <Title>Tumbuh Kembang</Title>
        <Subtitle>Kelola fitur dan konten tumbuh kembang anak</Subtitle>
      </PageHeader>

      {!selectedFeature ? (
        <>
          <FeaturesGrid>
            {features.length === 0 ? (
              <EmptyState>Belum ada fitur tumbuh kembang</EmptyState>
            ) : (
              features.map((feature) => (
                <FeatureCard 
                  key={feature._id}
                  $color={feature.color}
                  onClick={() => fetchContent(feature.slug)}
                >
                  <FeatureEditBtn onClick={(e) => {
                    e.stopPropagation();
                    setEditingFeature(feature);
                  }}>
                    ✏️ Edit
                  </FeatureEditBtn>
                  <FeatureIcon $bg={featureColors[feature.slug] || feature.color}>
                    {featureIcons[feature.slug] || feature.icon || '📋'}
                  </FeatureIcon>
                  <FeatureName>
                    {feature.name}
                    {feature.slug === 'milestone' && <ComingSoonBadge style={{ marginLeft: 8 }}>Segera Hadir</ComingSoonBadge>}
                    {feature.isPremium && <PremiumBadge style={{ marginLeft: 8 }}>💎 Premium</PremiumBadge>}
                  </FeatureName>
                  <FeatureDesc>{feature.description || 'Klik untuk melihat konten'}</FeatureDesc>
                  <FeatureMeta>
                    <ContentCount>
                      📄 {contentCounts[feature.slug] || 0} konten
                    </ContentCount>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <StatusDot $active={feature.isActive} />
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        {feature.isActive ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </div>
                  </FeatureMeta>
                </FeatureCard>
              ))
            )}
          </FeaturesGrid>
        </>
      ) : (
        <Section>
          <SectionHeader>
            <SectionTitle>
              {featureIcons[selectedFeature] || '📋'} {selectedFeatureData?.name || selectedFeature}
            </SectionTitle>
            <BackBtn onClick={() => { setSelectedFeature(null); setContent([]); }}>
              ← Kembali
            </BackBtn>
          </SectionHeader>

          {loadingContent ? (
            <LoadingState>Loading konten...</LoadingState>
          ) : isImunisasi ? (
            renderImunisasiContent()
          ) : content.length === 0 ? (
            <EmptyState>Belum ada konten untuk fitur ini</EmptyState>
          ) : isStimulasi ? (
            renderStimulasiContent()
          ) : isMPASI ? (
            renderMPASIContent()
          ) : (
            renderDefaultContent()
          )}
        </Section>
      )}

      {/* Edit Stimulasi Modal */}
      {editingItem && (
        <EditStimulasiModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={handleItemSaved}
        />
      )}

      {/* Edit MPASI Modal */}
      {editingMPASI && (
        <EditMPASIModal
          item={editingMPASI}
          onClose={() => setEditingMPASI(null)}
          onSaved={handleMPASISaved}
        />
      )}

      {/* Edit Feature Modal */}
      {editingFeature && (
        <EditFeatureModal
          feature={editingFeature}
          onClose={() => setEditingFeature(null)}
          onSaved={handleFeatureSaved}
        />
      )}
    </>
  );
};

export default TumbuhKembangPage;
